/**
 * Layout of an embedding: what the page does differently when a host app
 * frames it. Components ask for one capability, never for a theme by name
 * (`isPolarisEmbed()` stays for the host contract only), so a second host
 * theme picks its own mix and a host switches single values by URL.
 *
 * Order, last wins: reference layout → embedded (a Seller iframe start: no
 * wordmark) → the embedding theme's preset → single iframe parameters
 * (docs/guides/BRANDING_API_IFRAME.md).
 *
 * The parameters are unsigned — the iframe signature covers the login only —
 * so they may steer looks and arrangement, never functions, prices or
 * rights: values come from fixed lists, numbers are clamped, anything else is
 * ignored, and no parameter carries a URL or CSS. Like the theme they count
 * only on a Seller iframe start (lib/iframe-boot.ts records them there).
 */
import { currentEmbedTheme, type EmbedTheme } from './embed-theme';
import {
  hasRecordedIframeStart,
  readIframeParams,
  type IframeParams,
} from './iframe-boot';

export type TileStyle = 'classic' | 'media';
export type Density = 'comfortable' | 'compact';
export type RadiusScale = 'sharp' | 'soft' | 'round';

export interface EmbedLayout {
  /**
   * Fold secondary UI so a step fits a short host frame: the procedure bar
   * after an upload, the motif row down to its pixel size, the print quality
   * on a line of its own.
   */
  foldSecondary: boolean;
  /** Explanations sit behind an info button instead of running text. */
  helpAsTooltip: boolean;
  /**
   * Catalogue tiles: `classic` has a switch and area chips below the photo,
   * `media` a check mark and the area widths as a badge on the photo.
   */
  tileStyle: TileStyle;
  /**
   * No DesignDeck wordmark: the host names the app. Off at top level, on in
   * every Seller iframe; `?logo=show` brings it back.
   */
  hideBrand: boolean;
  /**
   * The host owns the page chrome and the navigation: no seller shell header,
   * no way to the design overview; the guided creator is the home, and the
   * classic editor and Bulk Creation are reached through its advanced link.
   */
  hostChrome: boolean;
  /** Spacing of the creator's layout roles (steps, panels, top bar). */
  density: Density;
  /** Radius scale; null keeps the radii of the reference or the theme. */
  radius: RadiusScale | null;
  /** Minimum tile width in CSS px; null keeps the layout's own grid. */
  tileMin: number | null;
}

export const REFERENCE_LAYOUT: Readonly<EmbedLayout> = Object.freeze({
  foldSecondary: false,
  helpAsTooltip: false,
  tileStyle: 'classic',
  hideBrand: false,
  hostChrome: false,
  density: 'comfortable',
  radius: null,
  tileMin: null,
});

const THEME_PRESETS: Record<EmbedTheme, Readonly<EmbedLayout>> = {
  // The Shopify admin frames the creator in a short, fixed-height frame with
  // its own title bar and navigation.
  polaris: Object.freeze({
    foldSecondary: true,
    helpAsTooltip: true,
    tileStyle: 'media',
    hideBrand: true,
    hostChrome: true,
    density: 'compact',
    radius: null,
    // Four columns in a ~1080 px admin frame.
    tileMin: 170,
  }),
};

export const TILE_MIN_RANGE = { min: 140, max: 320 } as const;

/** Radius tokens per scale (chip, control, card, section). */
const RADIUS_SCALES: Record<
  RadiusScale,
  readonly [number, number, number, number]
> = {
  sharp: [2, 4, 6, 8],
  soft: [6, 10, 14, 20],
  round: [10, 16, 22, 28],
};
const RADIUS_TOKENS = [
  '--radius-sm',
  '--radius',
  '--radius-md',
  '--radius-lg',
] as const;

function oneOf<T extends string>(
  value: string | undefined,
  allowed: readonly T[]
): T | undefined {
  return allowed.find((candidate) => candidate === value);
}

/**
 * The layout values a host set by URL, validated: unknown keys and invalid
 * values are dropped, `tileMin` is clamped to its range.
 */
export function parseLayoutParams(params: IframeParams): Partial<EmbedLayout> {
  const layout: Partial<EmbedLayout> = {};

  const density = oneOf(params.density, ['comfortable', 'compact'] as const);
  if (density) layout.density = density;

  const radius = oneOf(params.radius, ['sharp', 'soft', 'round'] as const);
  if (radius) layout.radius = radius;

  if (params.tileMin !== undefined && /^\d{1,4}$/.test(params.tileMin)) {
    layout.tileMin = Math.min(
      TILE_MIN_RANGE.max,
      Math.max(TILE_MIN_RANGE.min, Number(params.tileMin))
    );
  }

  // `host`: the host brings title bar, navigation and brand.
  const chrome = oneOf(params.chrome, ['full', 'host'] as const);
  if (chrome) layout.hostChrome = chrome === 'host';
  if (chrome === 'host') layout.hideBrand = true;

  // The wordmark on its own; it wins over `chrome` and the theme.
  const logo = oneOf(params.logo, ['show', 'hide'] as const);
  if (logo) layout.hideBrand = logo === 'hide';

  return layout;
}

/** Inside a Seller iframe the host names the app: no wordmark by default. */
const EMBEDDED_LAYOUT: Readonly<EmbedLayout> = Object.freeze({
  ...REFERENCE_LAYOUT,
  hideBrand: true,
});

/**
 * The layout of a page view: the reference (or, `embedded`, the iframe
 * default), the theme's preset, then the host's parameters on top.
 */
export function resolveEmbedLayout(
  theme: EmbedTheme | null,
  params: IframeParams = {},
  embedded = false
): EmbedLayout {
  const base = embedded ? EMBEDDED_LAYOUT : REFERENCE_LAYOUT;
  return {
    ...(theme ? THEME_PRESETS[theme] : base),
    ...parseLayoutParams(params),
  };
}

/**
 * The parameters of this page view and whether it is a Seller iframe: from
 * the URL of the start, otherwise from what that start recorded.
 */
function currentEmbedding(): { params: IframeParams; embedded: boolean } {
  if (typeof window === 'undefined') return { params: {}, embedded: false };
  const url = new URLSearchParams(window.location.search);
  if (url.get('isIframe') === 'true' && url.get('key')) {
    return { params: Object.fromEntries(url.entries()), embedded: true };
  }
  return { params: readIframeParams(), embedded: hasRecordedIframeStart() };
}

/** The layout of this page view, from the theme applied at boot. */
export function embedLayout(): EmbedLayout {
  const { params, embedded } = currentEmbedding();
  return resolveEmbedLayout(currentEmbedTheme(), params, embedded);
}

/**
 * Puts the layout values that CSS reads on <html>: `data-embed-density`, the
 * tile minimum (`--dc-tile-min` + `data-embed-tile-min`) and a radius scale
 * as inline tokens, which win over the theme's stylesheet. Call once at boot,
 * after `applyEmbedTheme()`.
 */
export function applyEmbedLayout(): EmbedLayout {
  const layout = embedLayout();
  if (typeof document === 'undefined') return layout;
  const root = document.documentElement;

  if (layout.density === 'compact') root.dataset.embedDensity = 'compact';
  else delete root.dataset.embedDensity;

  if (layout.tileMin !== null) {
    root.dataset.embedTileMin = '';
    root.style.setProperty('--dc-tile-min', `${layout.tileMin}px`);
  } else {
    delete root.dataset.embedTileMin;
    root.style.removeProperty('--dc-tile-min');
  }

  const scale = layout.radius ? RADIUS_SCALES[layout.radius] : null;
  RADIUS_TOKENS.forEach((token, index) => {
    if (scale) root.style.setProperty(token, `${scale[index]}px`);
    else root.style.removeProperty(token);
  });

  return layout;
}
